import React, { useState } from 'react';
import Button from '@components/common/Button';

export interface NR301Values {
  legal_name: string;
  country_of_residence: string;
  foreign_tax_id: string;
  itn?: string;
  treaty_country: string;
  treaty_article: string;
  treaty_rate_claimed: string;
  signature_full_name: string;
}

interface Props {
  onSubmit: (values: NR301Values) => Promise<void> | void;
  isPending?: boolean;
}

const inputClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

export function NR301Form({ onSubmit, isPending = false }: Props) {
  const [v, setV] = useState<NR301Values>({
    legal_name: '',
    country_of_residence: 'US',
    foreign_tax_id: '',
    itn: '',
    treaty_country: 'US',
    treaty_article: '',
    treaty_rate_claimed: '0.0000',
    signature_full_name: '',
  });
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const country = v.country_of_residence.trim().toUpperCase();
    const treatyCountry = v.treaty_country.trim().toUpperCase();
    if (country.length !== 2) {
      setError('Country of residence must be an ISO alpha-2 code.');
      return;
    }
    if (treatyCountry.length !== 2) {
      setError('Treaty country must be an ISO alpha-2 code.');
      return;
    }

    const rate = Number(v.treaty_rate_claimed);
    if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
      setError('Claimed withholding rate must be a decimal between 0 and 1.');
      return;
    }

    await onSubmit({
      ...v,
      legal_name: v.legal_name.trim(),
      country_of_residence: country,
      foreign_tax_id: v.foreign_tax_id.trim(),
      itn: (v.itn ?? '').trim() || undefined,
      treaty_country: treatyCountry,
      treaty_article: v.treaty_article.trim(),
      treaty_rate_claimed: v.treaty_rate_claimed,
      signature_full_name: v.signature_full_name.trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 nr301-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Declaration of Eligibility for Treaty Benefits (NR301)
        </h2>
        <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
          Non-resident vendors complete this declaration so we can apply the correct treaty
          withholding rate on Canadian-source payments.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className={`${labelClass} md:col-span-2`}>
          Legal name<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.legal_name}
            onChange={(e) => setV({ ...v, legal_name: e.target.value })}
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          Country of residence (ISO alpha-2)<span className="text-red-500">*</span>
          <input
            type="text"
            required
            maxLength={2}
            value={v.country_of_residence}
            onChange={(e) =>
              setV({ ...v, country_of_residence: e.target.value.toUpperCase() })
            }
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          Foreign Tax ID<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.foreign_tax_id}
            onChange={(e) => setV({ ...v, foreign_tax_id: e.target.value })}
            className={inputClass}
          />
        </label>

        <label className={`${labelClass} md:col-span-2`}>
          Canadian ITN (optional)
          <input
            type="text"
            value={v.itn ?? ''}
            onChange={(e) => setV({ ...v, itn: e.target.value })}
            className={inputClass}
          />
        </label>
      </div>

      <fieldset className="rounded-lg border border-gray-200 p-4">
        <legend className="px-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Treaty benefits
        </legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className={labelClass}>
            Treaty country<span className="text-red-500">*</span>
            <input
              type="text"
              required
              maxLength={2}
              value={v.treaty_country}
              onChange={(e) =>
                setV({ ...v, treaty_country: e.target.value.toUpperCase() })
              }
              className={inputClass}
            />
          </label>
          <label className={labelClass}>
            Article<span className="text-red-500">*</span>
            <input
              type="text"
              required
              placeholder="e.g., XII(2)"
              value={v.treaty_article}
              onChange={(e) => setV({ ...v, treaty_article: e.target.value })}
              className={inputClass}
            />
          </label>
          <label className={`${labelClass} md:col-span-2`}>
            Claimed withholding rate (decimal, e.g., 0.1000 for 10%)
            <span className="text-red-500">*</span>
            <input
              type="number"
              required
              step="0.0001"
              min="0"
              max="1"
              value={v.treaty_rate_claimed}
              onChange={(e) => setV({ ...v, treaty_rate_claimed: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>
      </fieldset>

      <label className={labelClass}>
        Signature (full name)<span className="text-red-500">*</span>
        <input
          type="text"
          required
          value={v.signature_full_name}
          onChange={(e) => setV({ ...v, signature_full_name: e.target.value })}
          className={inputClass}
        />
      </label>

      <div className="flex justify-end border-t border-gray-200 pt-4">
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Submitting...' : 'Certify and Submit NR301'}
        </Button>
      </div>
    </form>
  );
}

export default NR301Form;
